/**
 * Виджет «Осмотры» (карточка поля v3, вкладка «Обзор», PR-B).
 *
 * 3 последних осмотра + «все →» (открывает подэкран-витрину AK-389). Строка:
 * дата · сводка находок / «проблем нет» · автор · счётчик фото. Данные —
 * GET /inspections?field_id=… (общий кэш с вкладкой «Осмотры»). Осмотры
 * создаются в мобильном/голосовом скаутинге — здесь только чтение.
 */
import { ChevronRight, Camera, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFieldInspections } from './widgetApi';
import { latestN, formatShortDate } from './widgetLogic';
import {
  findingsSummary,
  inspectorLabel,
  photoCount,
} from '../../inspections/inspectionLogic';

export function InspectionsWidget({
  fieldId,
  onOpenAll,
}: {
  fieldId: string;
  onOpenAll: () => void;
}) {
  const { data, isLoading, isError } = useFieldInspections(fieldId);
  const rows = latestN(data, 3);

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="flex items-center gap-2 leading-6">
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
            Осмотры
          </CardTitle>
          {(data?.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-8 px-2 text-primary"
              onClick={onOpenAll}
            >
              все
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription>Не удалось загрузить осмотры.</AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Осмотров пока нет — создаются в мобильном приложении.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((insp) => {
              const summary = findingsSummary(insp);
              const nPhotos = photoCount(insp);
              return (
                <li key={insp.id} className="flex items-center gap-3 py-2">
                  <span className="w-11 shrink-0 text-xs tabular-nums text-muted-foreground">
                    {formatShortDate(insp.inspected_at)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {summary.tone === 'ok' ? (
                        <span className="text-primary">Проблем нет</span>
                      ) : summary.tone === 'problem' ? (
                        summary.text
                      ) : (
                        'Осмотр'
                      )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {inspectorLabel(insp)}
                      {nPhotos > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1">
                          <Camera className="h-3.5 w-3.5" aria-hidden />
                          {nPhotos}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default InspectionsWidget;
